import {useApolloClient} from '@apollo/client';
import {LogViewer} from '@wandb/weave/common/components/Log/LogViewer';
import {Alert} from '@wandb/weave/components/Alert';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import gql from 'graphql-tag';
import React, {useEffect, useMemo, useState} from 'react';

import {Empty} from '../common/Empty';
import {CallSchema} from '../wfReactInterface/wfDataModelHooksInterface';

const RUN_LOG_QUERY = gql`
  query RunLogLines(
    $projectName: String!
    $entityName: String
    $runName: String!
  ) {
    project(name: $projectName, entityName: $entityName) {
      id
      run(name: $runName) {
        id
        logLines(last: 10000) {
          edges {
            node {
              id
              line
              level
              label
              timestamp
            }
          }
        }
        outputLogFile: files(first: 1, names: ["output.log"]) {
          edges {
            node {
              id
              sizeBytes
              url
            }
          }
        }
      }
    }
  }
`;

interface LogLineNode {
  id: string;
  label?: string;
  line: string;
  level: string;
  timestamp: string;
}

interface CallLogsProps {
  call: CallSchema;
}

// Parse wb_run_id format: entity/project/run_name
const parseWbRunId = (
  wbRunId: string
): {entity: string; project: string; runName: string} | null => {
  const parts = wbRunId.split('/');
  if (parts.length !== 3) {
    return null;
  }
  const [entity, project, runName] = parts;
  return {entity, project, runName};
};

export const CallLogs: React.FC<CallLogsProps> = ({call}) => {
  const apolloClient = useApolloClient();
  const [logLines, setLogLines] = useState<LogLineNode[]>([]);
  const [outputLogFileUrl, setOutputLogFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const wbRunId = call.traceCall?.wb_run_id;
  const callStartTime = call.rawSpan.start_time_ms;
  const callEndTime = call.rawSpan.end_time_ms;

  const parsedRunId = useMemo(() => {
    if (!wbRunId) {
      return null;
    }
    return parseWbRunId(wbRunId);
  }, [wbRunId]);

  useEffect(() => {
    if (!parsedRunId) {
      setError('Invalid W&B run ID format');
      setLoading(false);
      return;
    }

    // Reset state when call changes
    setLoading(true);
    setError(null);
    setLogLines([]);
    setOutputLogFileUrl(null);

    apolloClient
      .query({
        query: RUN_LOG_QUERY as any,
        variables: {
          entityName: parsedRunId.entity,
          projectName: parsedRunId.project,
          runName: parsedRunId.runName,
        },
        fetchPolicy: 'network-only', // Always fetch fresh data
      })
      .then(result => {
        const edges = result.data?.project?.run?.logLines?.edges || [];
        const lines = edges.map((edge: any) => edge.node);
        setLogLines(lines);

        // Extract output log file URL
        const outputLogFile = result.data?.project?.run?.outputLogFile;
        const firstNode = outputLogFile?.edges[0]?.node;
        const url =
          firstNode != null && firstNode.sizeBytes !== 0
            ? (firstNode.url ?? null)
            : null;
        setOutputLogFileUrl(url);

        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching logs:', err);
        setError('Failed to fetch logs');
        setLoading(false);
      });
  }, [apolloClient, parsedRunId, call.callId]);

  // Filter logs by call timestamp range
  const filteredLines = useMemo(() => {
    if (!callStartTime || !callEndTime) {
      return logLines;
    }

    const filtered = logLines.filter(line => {
      // Ensure the timestamp is parsed as UTC
      const timestamp = line.timestamp.endsWith('Z')
        ? line.timestamp
        : `${line.timestamp}Z`;
      const lineTimestamp = new Date(timestamp).getTime();

      return lineTimestamp >= callStartTime && lineTimestamp <= callEndTime;
    });

    return filtered;
  }, [logLines, callStartTime, callEndTime]);

  if (error) {
    return (
      <Tailwind style={{height: '100%', padding: '16px'}}>
        <Alert severity="error">{error}</Alert>
      </Tailwind>
    );
  }

  if (!loading && filteredLines.length === 0) {
    return (
      <Empty
        icon="list-bullets"
        heading="No logs found"
        description="There are no logs for this call during its execution time."
        moreInformation=""
      />
    );
  }

  return (
    <LogViewer
      loading={loading}
      lines={filteredLines}
      outputLogFile={outputLogFileUrl}
    />
  );
};
